/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/budgets-finance.md and
 * invoicing.md Marketing Constraints.
 *
 * IP to protect (do NOT expose on this page):
 *   - Exact MORS derivation formula
 *   - Revenue recognition resolution order (budget → org → default)
 *   - record_payment_safely RPC name
 *   - cost_type inheritance via lineItemTypeToCostType()
 *   - pricing-cascade.ts 5-level cascade internals
 *   - Dashboard metric formulas
 *
 * Master budget is STATIC — never mutated by COs or actuals. Actuals
 * are read-only overlays. Do not imply budgets auto-update.
 *
 * QuickBooks integration exists but maturity is unverified — do NOT
 * promise two-way QB sync. The prior page overclaimed this.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  BarChart3,
  Receipt,
  Layers,
  DollarSign,
  Briefcase,
  Calculator,
  TrendingUp,
  Repeat,
  Hammer,
  Lock,
  Percent,
  Building2,
  Banknote,
  ShieldCheck,
  FileText,
  ClipboardList,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Budgets & Invoices",
  title: "Protect your margin",
  titleHighlight: "from budget to final payment.",
  description:
    "Plan job financials with confidence, invoice the way your contracts actually work, and track cash flow without losing sight of original budget targets.",

  featureGrid: {
    title: "Everything the spreadsheet can't do",
    items: [
      {
        icon: Percent,
        title: "5 Overhead Recovery Methods",
        description:
          "Percentage of direct costs, percentage of revenue, fixed amount, activity-based allocation, or labor-hour absorption. Pick what fits your business — not a one-size-fits-all markup.",
      },
      {
        icon: TrendingUp,
        title: "Activity-Based Allocation",
        description:
          "Our activity-based overhead method understands that a maintenance job and a design-build project carry overhead differently. Per-category rates derived from your actual service mix.",
      },
      {
        icon: Layers,
        title: "Budget Line Hierarchy",
        description:
          "Groups and children with auto-summing parent rows. A proper chart of accounts — income, COGS, expense — with indentation and rollups that match how contractors already think.",
      },
      {
        icon: BarChart3,
        title: "Read-Only Actuals Overlay",
        description:
          "Actual costs and invoiced revenue show up as a variance overlay on your plan. Your budget stays clean. Variance is visible. The plan is never silently rewritten.",
      },
      {
        icon: Hammer,
        title: "Progress Billing with Retainage",
        description:
          "Draw-based invoicing per work area. Completion-percentage math. Retainage withheld on every draw and released through a dedicated flow. First-class — not a bolted-on afterthought.",
      },
      {
        icon: Repeat,
        title: "Service Billing",
        description:
          "Period-based billing that aggregates completed visits and their costs into a single invoice. Recurring maintenance work never falls through the cracks.",
      },
      {
        icon: Banknote,
        title: "Configurable Revenue Recognition",
        description:
          "Choose how revenue is recognized (paid in full, paid in part, or invoiced) plus retainage timing rules so reporting matches your accounting approach.",
      },
      {
        icon: Lock,
        title: "Over-Invoicing Prevention",
        description:
          "Built-in guardrails prevent billing above remaining estimate value, even during busy operations.",
      },
      {
        icon: Building2,
        title: "Company-Wide Budget Rollup",
        description:
          "Multiple divisions feed into a single grandmaster view. See the whole business or drill into residential, commercial, design-build, or maintenance.",
      },
    ],
  },

  workflow: {
    title: "What the financial lifecycle looks like in CC",
    description:
      "A practical flow from budget planning to payment collection and financial review.",
    steps: [
      {
        label: "Plan",
        title: "Set your budget and overhead strategy",
        description:
          "Set your pricing assumptions once so estimates start from a consistent financial baseline.",
        details: [
          "Multiple overhead strategy options",
          "Cost-type-aware pricing setup",
          "Reusable assumptions for estimating consistency",
          "Division-aware financial planning support",
          "Controlled override flexibility when needed",
        ],
      },
      {
        label: "Execute",
        title: "Actuals flow in as a read-only overlay",
        description:
          "Track plan vs actual during execution so overspend risks show up while there is still time to correct course.",
        details: [
          "Division and cost-type variance visibility",
          "Project and service cost outcomes reflected",
          "Change-order-aware financial context",
          "Original budget reference preserved",
          "In-flight variance awareness for managers",
        ],
      },
      {
        label: "Bill",
        title: "Three ways to invoice",
        description:
          "Use billing workflows that match your contract type, whether one-off, progress-draw, or recurring service.",
        details: [
          "Standalone invoice support",
          "Progress-draw billing with retainage handling",
          "Service-period billing support",
          "Structured retainage release flow",
          "Clear payment-term behavior",
          "Consistent billing history across job types",
        ],
      },
      {
        label: "Collect",
        title: "Record payments with no double-booking",
        description:
          "Record payments clearly and keep invoice status accurate so receivables stay reliable.",
        details: [
          "Safe payment-recording workflow",
          "Partial and full payment status visibility",
          "Retainage-aware payment handling",
          "Online payment support where configured",
          "Manual payment method support",
        ],
      },
      {
        label: "Recognize",
        title: "Revenue hits the right month",
        description:
          "Align financial reporting behavior with how your business recognizes revenue and retainage.",
        details: [
          "Configurable recognition policy support",
          "Retainage treatment options",
          "Division-level reporting compatibility",
          "Cleaner period reporting outcomes",
          "Better finance-team alignment",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: TrendingUp,
      title: "Flexible Overhead Strategy",
      description:
        "Use overhead methods that reflect how your business actually operates so pricing stays realistic across different job types.",
      bullets: [
        "Multiple overhead approaches available",
        "Supports varied service and project mixes",
        "Improves pricing consistency",
        "Feeds estimate assumptions",
        "Practical for teams with mixed job portfolios",
      ],
    },
    {
      icon: Hammer,
      title: "Progress Billing and Retainage Control",
      description:
        "Handle progress draws and retainage in a structured way so billing stays aligned with contract requirements and project progress.",
      bullets: [
        "Progress-based invoice workflow support",
        "Retainage withholding and release structure",
        "Clear draw history tracking",
        "Safer close-out billing",
        "Stronger financial audit readiness",
        "Better owner/client billing transparency",
      ],
    },
    {
      icon: Receipt,
      title: "One System for Multiple Billing Models",
      description:
        "Keep billing workflows consistent across standalone, progress, and service invoicing without fragmenting your process.",
      bullets: [
        "Supports one-off and recurring billing needs",
        "Progress invoicing for construction contracts",
        "Service-period invoice support",
        "Shared payment and status handling",
        "Consistent operational training for teams",
        "Less process drift between departments",
      ],
    },
    {
      icon: ShieldCheck,
      title: "Safer Collections and Billing Guardrails",
      description:
        "Use built-in billing guardrails to reduce costly errors in invoicing and payment recording.",
      bullets: [
        "Payment-entry safety protections",
        "Over-billing prevention controls",
        "Retainage release safeguards",
        "Cleaner reconciliation outcomes",
        "Higher trust in receivables data",
      ],
    },
  ],

  connections: {
    title: "How budgets and invoices connect to the rest of the platform",
    items: [
      {
        icon: Calculator,
        flow: "Budget → Estimate",
        description:
          "Budget assumptions — overhead method, margin, contingency, burden — snapshot into every estimate at creation. Per-estimate overrides are supported without mutating the budget itself.",
      },
      {
        icon: Briefcase,
        flow: "Project → Actuals Overlay",
        description:
          "Time entries, PO receipts, and manual cost entries flow into a read-only overlay on the budget. You see variance per division and per cost type. The plan stays clean.",
      },
      {
        icon: Hammer,
        flow: "Completion % → Progress Invoice",
        description:
          "Work-area completion percentages drive progress billing. Completed-to-date minus previously-billed minus retainage becomes the draw amount. Automatic, verifiable, auditable.",
      },
      {
        icon: Banknote,
        flow: "Invoice Payment → Recognized Revenue",
        description:
          "Payments feed budget rollups based on your selected revenue policy, with retainage timing handled separately.",
      },
      {
        icon: DollarSign,
        flow: "PO Receive → Pricing Signals",
        description:
          "When you receive a PO and the actual unit cost differs from the estimated cost, CC captures the variance as a pricing signal you can use to sharpen future estimates.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Cost Database & Work Packages",
      href: "/features/cost-database",
      icon: ClipboardList,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Cost Management",
      href: "/features/cost-database",
      icon: DollarSign,
    },
    {
      label: "Documents",
      href: "/features/documents",
      icon: Receipt,
    },
  ],
};

export function BudgetsInvoicesPageClient() {
  return <FeaturePageTemplate config={config} />;
}
